// Module shell: Family + per-class teacher destinations (+ Admin in mock).
//
// Nav model: no "Teacher" abstraction — each class a person teaches is its
// own nav destination (type icon + type label + class name). Family and
// Admin are single destinations. Nav chrome renders only when the login has
// more than one destination.
//
// A class page's header card IS the class identity (name over
// "type · day · room · location"), the same two-line structure as the
// family header, so nothing repeats below it. Location never appears on
// students inside a class roster — one class = one church, so it lives on
// the class alone. Admin keeps its own stable header while drilling.
//
// Desktop (≥900px): collapsible sidebar + roster tables.
// Mobile (<900px): bottom tab bar + card lists.
//
// Mock mode resolves modules from mockDataV2 by phone. Live mode uses
// teacherClasses / hasFamily from App (GET /family + GET /class).
import { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaArrowLeft,
  FaBirthdayCake,
  FaBook,
  FaCalendarAlt,
  FaChalkboardTeacher,
  FaChevronDown,
  FaChevronRight,
  FaDoorOpen,
  FaClipboardList,
  FaFemale,
  FaGlobeEurope,
  FaMale,
  FaMapMarkerAlt,
  FaMusic,
  FaPhone,
  FaSearch,
  FaSignOutAlt,
  FaSort,
  FaSortDown,
  FaSortUp,
  FaUser,
  FaUserFriends,
} from 'react-icons/fa'
import LanguageToggle from '../Common/LanguageToggle'
import { useSignOutConfirm } from '../Common/ConfirmSignOut'
import { useLanguage } from '../../context/LanguageContext'
import { dayLabel, useTranslation } from '../../utils/translations'
import StudentGroupedView from '../Data/StudentGroupedView'
import { DEMO_ALL_MODULES_PHONE, resolveModules } from '../../data/mockDataV2'
import { navigate, slugify, usePathname } from '../../hooks/useRoute'
import { isUsingMockData } from '../../services/apiService'
import './ModulesConcept.css'

/* Type labels live in translations.modules.types; resolved through t() so the
   nav, chips and headers follow the language toggle. */
const typeLabel = (t, type) => t(`modules.types.${type}`)

// Type icons match the family view's activity headers (FaBook/FaGlobeEurope/FaMusic)
const TYPE_ICONS = {
  sundaySchool: FaBook,
  russianSchool: FaGlobeEurope,
  childrensChoir: FaMusic,
}

const MODULE_TYPE_ORDER = ['sundaySchool', 'russianSchool', 'childrensChoir']

/* The schema's class identity is the bare "class" value ("21", "1В"); for
   choirs it holds the choir number. Prefix it so labels read as names. */
const classWords = (t) => ({ classWord: t('modules.classPrefix'), choirWord: t('modules.choirPrefix') })
const classNameWith = (cls, { classWord, choirWord }) =>
  cls.type === 'childrensChoir' ? `${choirWord} ${cls.class}` : `${classWord} ${cls.class}`
const classDisplayName = (cls, t) => classNameWith(cls, classWords(t))

/* Choir classes have leaders, not teachers. */
const teachersLabel = (t, cls) =>
  t(cls.type === 'childrensChoir' ? 'modules.leaders' : 'modules.teachers')

/* Russian has three plural forms (1 ученик / 2 ученика / 5 учеников); English two.
   Returns the One/Few/Many suffix of the matching translation key. */
const pluralForm = (n, language) => {
  if (language !== 'ru') return n === 1 ? 'One' : 'Many'
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'One'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'Few'
  return 'Many'
}

const studentsCount = (t, language, n) =>
  t(`modules.studentsCount${pluralForm(n, language)}`, { n })

/* Legacy "teachers" names array preferred; falls back to teacherV2. */
const teacherNames = (cls) =>
  cls.teachers && cls.teachers.length > 0
    ? cls.teachers
    : (cls.teacherV2 || []).map((t) => t.teacherName)

/* Schema students/families carry denormalized parentName1/2 + parentPhone1/2
   (phone may be an empty string until known). */
const parentPairs = (o) => {
  const pairs = []
  if (o.parentName1) pairs.push({ name: o.parentName1, phone: o.parentPhone1 || '' })
  if (o.parentName2) pairs.push({ name: o.parentName2, phone: o.parentPhone2 || '' })
  return pairs
}

/* Names are "Фамилия Имя" (surname first) — the given name follows. */
const givenName = (name) => String(name || '').trim().split(/\s+/)[1] || String(name || '')

const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(
    () => window.matchMedia('(min-width: 900px)').matches
  )
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 900px)')
    const onChange = (e) => setIsDesktop(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return isDesktop
}

const formatBirthdate = (iso, language = 'en') => {
  const [y, m, d] = String(iso).split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(language === 'ru' ? 'ru-RU' : 'en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

const ageOf = (iso) => {
  const [y, m, d] = String(iso).split('-').map(Number)
  const now = new Date()
  let age = now.getFullYear() - y
  if (now.getMonth() + 1 < m || (now.getMonth() + 1 === m && now.getDate() < d)) age -= 1
  return age
}

const LocationBadge = ({ location }) =>
  location ? <span className="mc-badge">{location}</span> : null

LocationBadge.propTypes = { location: PropTypes.string }

const initialsOf = (name) => {
  const parts = String(name || '').trim().split(/\s+/)
  return ((parts[0]?.[0] || '') + (parts[parts.length - 1]?.[0] || '')).toUpperCase()
}

/* Leading visual for people rows: initials in a circle, tinted by gender —
   anchors the row and makes gender readable at a glance without a symbol. */
const Avatar = ({ name, gender, large = false }) => (
  <span className={`mc-avatar mc-avatar-${gender || 'unknown'} ${large ? 'mc-avatar-lg' : ''}`} aria-hidden="true">
    {initialsOf(name)}
  </span>
)

Avatar.propTypes = {
  name: PropTypes.string.isRequired,
  gender: PropTypes.string,
  large: PropTypes.bool,
}

const GenderSymbol = ({ gender }) => {
  const { t } = useTranslation()
  if (gender === 'male') return <FaMale className="mc-gender-symbol mc-gender-symbol-male" aria-label={t('modules.boy')} />
  if (gender === 'female') return <FaFemale className="mc-gender-symbol mc-gender-symbol-female" aria-label={t('modules.girl')} />
  return null
}

GenderSymbol.propTypes = { gender: PropTypes.string }

const TypeIcon = ({ type, small }) => {
  const Icon = TYPE_ICONS[type]
  return (
    <span className={`mc-type-icon mc-type-${type} ${small ? 'mc-type-icon-sm' : ''}`}>
      <Icon />
    </span>
  )
}

TypeIcon.propTypes = { type: PropTypes.string.isRequired, small: PropTypes.bool }

/* Person icon + number: this count means people. The tooltip / accessible
   name spells it out with the right plural ("12 students" / "12 учеников"). */
const PeopleCount = ({ n }) => {
  const { t } = useTranslation()
  const { language } = useLanguage()
  const label = studentsCount(t, language, n)
  return (
    <span className="mc-count" title={label} aria-label={label}>
      <FaUser aria-hidden="true" /> {n}
    </span>
  )
}

PeopleCount.propTypes = { n: PropTypes.number.isRequired }

/* ---------------------------------------------------------------- */
/* Headers — same card structure the family view renders            */
/* ---------------------------------------------------------------- */

const HeaderCard = ({ title, subtitle, onSignOut }) => {
  const { t } = useTranslation()
  const requestSignOut = useSignOutConfirm(onSignOut)
  return (
    <div className="family-header-card">
      <div className="header-with-logo">
        <img src="/aos-logo.png" alt={t('common.logoAlt')} className="family-logo desktop-logo" />
        <img src="/logo.svg" alt={t('common.logoAlt')} className="family-logo mobile-logo" />
        <div className="family-info">
          <div className="parent-names">
            <span className="parent-name">{title}</span>
          </div>
          <div className="family-subtitle">{subtitle}</div>
        </div>
        <div className="family-language-toggle">
          <LanguageToggle />
          {onSignOut && (
            <button
              type="button"
              className="header-signout"
              title={t('modules.signOut')}
              onClick={requestSignOut}
            >
              <FaSignOutAlt aria-hidden="true" />
              <span>{t('modules.signOut')}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

HeaderCard.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  onSignOut: PropTypes.func,
}

/* Labeled details bar — label-over-value clusters give the class facts
   hierarchy instead of one flat dot-separated line. */
const DetailTile = ({ icon: Icon, label, value, wide = false, className = '' }) => (
  <div className={`mc-detail-item ${wide ? 'mc-detail-teachers' : ''} ${className}`}>
    <span className="mc-detail-chip" aria-hidden="true">
      <Icon />
    </span>
    <span className="mc-detail-text">
      <span className="mc-detail-item-label">{label}</span>
      <span className="mc-detail-value">{value}</span>
    </span>
    <Icon className="mc-detail-ghost" aria-hidden="true" />
  </div>
)

DetailTile.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  wide: PropTypes.bool,
  className: PropTypes.string,
}

/* Foot of a module page on a phone: the same quiet sign-out the family page
   ends with, so every page in the shell ends the same way. Desktop signs out
   from the header card, so this renders nothing there (see the stylesheet).
   The family page is not given one - DataView already carries its own. */
const PageFoot = ({ onSignOut }) => {
  const { t } = useTranslation()
  const requestSignOut = useSignOutConfirm(onSignOut)
  if (!onSignOut) return null
  return (
    <div className="mc-page mc-page-foot">
      <button type="button" className="signout-bottom" onClick={requestSignOut}>
        <FaSignOutAlt aria-hidden="true" />
        {t('main.signOut')}
      </button>
    </div>
  )
}

PageFoot.propTypes = { onSignOut: PropTypes.func }

/* Day strings arrive as Russian caps from the live data ("СРЕДА"); render them
   in whichever language the toggle is on, falling back to the raw text. */
const DayName = ({ day }) => {
  const { language } = useLanguage()
  return dayLabel(day, language)
}

DayName.propTypes = { day: PropTypes.string }

const ClassDetails = ({ cls, inline = false }) => {
  const { t } = useTranslation()
  return (
    <div className={`mc-details ${inline ? 'mc-details-inline' : ''}`}>
      <DetailTile icon={FaCalendarAlt} label={t('modules.day')} value={<DayName day={cls.day} />} className="mc-tile-day" />
      <DetailTile icon={FaDoorOpen} label={t('modules.room')} value={cls.classroom} className="mc-tile-room" />
      {cls.location && (
        <DetailTile icon={FaMapMarkerAlt} label={t('modules.location')} value={cls.location} className="mc-tile-location" />
      )}
      <DetailTile icon={FaChalkboardTeacher} label={teachersLabel(t, cls)} value={teacherNames(cls).join(' · ')} wide />
    </div>
  )
}

ClassDetails.propTypes = { cls: PropTypes.object.isRequired, inline: PropTypes.bool }

/* ---------------------------------------------------------------- */
/* Roster — table on desktop, card list on mobile                   */
/* ---------------------------------------------------------------- */

/* Sort keys -> translation keys for their menu labels. */
const SORT_LABEL_KEYS = { name: 'modules.name', birthday: 'modules.birthday' }

const useRosterControls = (students) => {
  const [query, setQuery] = useState('')
  const [sortKey, setSortKey] = useState('name')
  const [sortDir, setSortDir] = useState('asc')

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase()
    const filtered = q
      ? students.filter((st) => st.studentName.toLowerCase().includes(q))
      : [...students]
    const compare = {
      name: (a, b) => a.studentName.localeCompare(b.studentName),
      birthday: (a, b) => a.birthdate.localeCompare(b.birthdate),
    }[sortKey]
    filtered.sort(compare)
    if (sortDir === 'desc') filtered.reverse()
    return filtered
  }, [students, query, sortKey, sortDir])

  return { rows, query, setQuery, sortKey, sortDir, toggleSort }
}

const SortTh = ({ label, sortId, sortKey, sortDir, onSort }) => {
  const active = sortKey === sortId
  return (
    <th
      className="mc-th-sort"
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
      onClick={() => onSort(sortId)}
    >
      {label}
      {active ? (
        sortDir === 'asc' ? (
          <FaSortUp className="mc-sort-icon mc-sort-active" />
        ) : (
          <FaSortDown className="mc-sort-icon mc-sort-active" />
        )
      ) : (
        <FaSort className="mc-sort-icon" />
      )}
    </th>
  )
}

SortTh.propTypes = {
  label: PropTypes.string.isRequired,
  sortId: PropTypes.string.isRequired,
  sortKey: PropTypes.string,
  sortDir: PropTypes.string,
  onSort: PropTypes.func.isRequired,
}

const RosterTable = ({ students, showClasses = false, showLocation = false, onOpenStudent, sortKey, sortDir, onSort }) => {
  const { t } = useTranslation()
  const { language } = useLanguage()
  return (
    <div className="mc-table-wrap">
      <table className="mc-table">
        <thead>
          <tr>
            {onSort ? (
              <>
                <SortTh label={t('modules.student')} sortId="name" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                <SortTh label={t('modules.birthday')} sortId="birthday" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              </>
            ) : (
              <>
                <th>{t('modules.student')}</th>
                <th>{t('modules.birthday')}</th>
              </>
            )}
            <th>{t('modules.age')}</th>
            {showLocation && <th>{t('modules.location')}</th>}
            {showClasses && <th>{t('modules.classes')}</th>}
            <th>{t('modules.parents')}</th>
          </tr>
        </thead>
        <tbody>
          {students.map((s) => (
            <tr
              key={s.childId || s.studentName}
              className={onOpenStudent ? 'mc-row-click' : undefined}
              onClick={onOpenStudent ? () => onOpenStudent(s.childId) : undefined}
            >
              <td className="mc-td-name">
                <span className="mc-name-cell">
                  <Avatar name={s.studentName} gender={s.gender} />
                  {s.studentName}
                </span>
              </td>
              <td className="mc-td-date">{formatBirthdate(s.birthdate, language)}</td>
              <td>{ageOf(s.birthdate)}</td>
              {showLocation && (
                <td>{s.location ? <LocationBadge location={s.location} /> : <span className="mc-muted">—</span>}</td>
              )}
              {showClasses && (
                <td>
                  <div className="mc-chips">
                    {s.classes.map((c) => (
                      <span key={c.classId} className={`mc-chip mc-type-${c.type}`}>
                        {classDisplayName(c, t)}
                      </span>
                    ))}
                  </div>
                </td>
              )}
              <td className="mc-td-parents">
                {parentPairs(s).map((p) => (
                  <span key={`${p.name}:${p.phone}`} className="mc-parent-pair">
                    {p.name}{' '}
                    {p.phone && (
                      <a href={`tel:${p.phone}`} onClick={(e) => e.stopPropagation()}>
                        {p.phone}
                      </a>
                    )}
                  </span>
                ))}
              </td>
            </tr>
          ))}
          {students.length === 0 && (
            <tr>
              <td className="mc-empty" colSpan={4 + (showClasses ? 1 : 0) + (showLocation ? 1 : 0)}>
                {t('modules.noStudents')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

RosterTable.propTypes = {
  students: PropTypes.array.isRequired,
  showClasses: PropTypes.bool,
  showLocation: PropTypes.bool,
  onOpenStudent: PropTypes.func,
  sortKey: PropTypes.string,
  sortDir: PropTypes.string,
  onSort: PropTypes.func,
}

const StudentRow = ({ student, classChips, showLocation = false, onOpen }) => {
  const { t } = useTranslation()
  const { language } = useLanguage()
  const [showParents, setShowParents] = useState(false)
  const parents = parentPairs(student)
  return (
    <li
      className={`mc-student ${onOpen ? 'mc-row-click' : ''}`}
      onClick={onOpen ? () => onOpen(student.childId) : undefined}
    >
      <div className="mc-student-head">
        <Avatar name={student.studentName} gender={student.gender} />
        <div className="mc-student-info">
          <span className="mc-student-name">{student.studentName}</span>
          <span className="mc-student-meta">
            <FaBirthdayCake className="mc-meta-icon" aria-hidden="true" />
            {formatBirthdate(student.birthdate, language)}
            <span className="mc-meta-sep">·</span>
            {t('modules.age')} {ageOf(student.birthdate)}
            {showLocation && <LocationBadge location={student.location} />}
          </span>
          {classChips && classChips.length > 0 && (
            <div className="mc-chips">
              {classChips.map((c) => (
                <span key={c.classId} className={`mc-chip mc-type-${c.type}`}>
                  {classDisplayName(c, t)}
                </span>
              ))}
            </div>
          )}
        </div>
        <span className="mc-student-trailing">
          {parents.length > 0 && (
            <button
              type="button"
              className="mc-call-btn"
              aria-expanded={showParents}
              aria-label={t('modules.showParentContacts', { n: parents.length })}
              onClick={(e) => {
                e.stopPropagation()
                setShowParents(!showParents)
              }}
            >
              <FaPhone />
              <span className="mc-call-count">{parents.length}</span>
            </button>
          )}
          {onOpen && <FaChevronRight className="mc-chevron" />}
        </span>
      </div>
      {showParents && (
        <ul className="mc-parents-list">
          {parents.map((p) => (
            <li key={`${p.name}:${p.phone}`} className="mc-parent-row">
              <span>{p.name}</span>
              {p.phone && (
                <a href={`tel:${p.phone}`} onClick={(e) => e.stopPropagation()}>
                  {p.phone}
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}

StudentRow.propTypes = {
  student: PropTypes.object.isRequired,
  classChips: PropTypes.array,
  showLocation: PropTypes.bool,
  onOpen: PropTypes.func,
}

const Roster = ({ students, showLocation = false }) => {
  const { t } = useTranslation()
  const isDesktop = useIsDesktop()
  const { rows, query, setQuery, sortKey, sortDir, toggleSort } = useRosterControls(students)
  const [menuOpen, setMenuOpen] = useState(false)
  const toolbarRef = useRef(null)

  const pickSort = (key) => {
    toggleSort(key)
    setMenuOpen(false)
  }

  // On phones the software keyboard covers the lower half of the screen;
  // scroll the toolbar to the top on focus so results stay visible. The
  // delay lets the keyboard animation start first (iOS Safari / Android
  // Chrome both settle correctly with this).
  const scrollSearchToTop = () => {
    if (isDesktop) return
    setTimeout(() => {
      toolbarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 300)
  }

  return (
    <>
      <div className="mc-roster-toolbar" ref={toolbarRef}>
        <div className="mc-search-wrap">
          <FaSearch aria-hidden="true" />
          <input
            type="search"
            className="mc-search"
            placeholder={t('modules.searchStudents')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={scrollSearchToTop}
          />
        </div>
        <div className="mc-sort">
          <button
            type="button"
            className="mc-sort-btn"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {t('modules.sortBy')}: {t(SORT_LABEL_KEYS[sortKey])}
            <FaChevronDown className={`mc-sort-caret ${menuOpen ? 'mc-sort-caret-open' : ''}`} />
          </button>
          {menuOpen && (
            <>
              <div className="mc-sort-backdrop" onClick={() => setMenuOpen(false)} />
              <div className="mc-sort-menu" role="menu">
                {Object.entries(SORT_LABEL_KEYS).map(([key, labelKey]) => (
                  <button
                    key={key}
                    type="button"
                    role="menuitem"
                    className={`mc-sort-option ${sortKey === key ? 'mc-sort-option-active' : ''}`}
                    onClick={() => pickSort(key)}
                  >
                    {t(labelKey)}
                    {sortKey === key &&
                      (sortDir === 'asc' ? <FaSortUp className="mc-sort-icon mc-sort-active" /> : <FaSortDown className="mc-sort-icon mc-sort-active" />)}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      {isDesktop ? (
        <section className="mc-panel">
          <RosterTable
            students={rows}
            showLocation={showLocation}
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={toggleSort}
          />
        </section>
      ) : (
        <ul className="mc-roster mc-roster-card">
          {rows.map((s) => (
            <StudentRow key={s.childId} student={s} showLocation={showLocation} />
          ))}
          {rows.length === 0 && <li className="mc-empty">{t('modules.noStudentsMatch', { q: query })}</li>}
        </ul>
      )}
    </>
  )
}

Roster.propTypes = { students: PropTypes.array.isRequired, showLocation: PropTypes.bool }

/* ---------------------------------------------------------------- */
/* Class page — one taught class as a nav destination.              */
/* The header card above it already carries the class identity.     */
/* ---------------------------------------------------------------- */

const ClassPage = ({ cls }) => (
  <div className="mc-page">
    <ClassDetails cls={cls} />
    <Roster students={cls.students} />
  </div>
)

ClassPage.propTypes = { cls: PropTypes.object.isRequired }

/* ---------------------------------------------------------------- */
/* Admin — class detail panel (keeps the Admin header while open)   */
/* ---------------------------------------------------------------- */

const ClassPanel = ({ cls }) => {
  const { t } = useTranslation()
  return (
    <section className="mc-panel">
      <header className="mc-panel-head">
        <TypeIcon type={cls.type} />
        <div className="mc-class-id">
          <span className="mc-class-type">{typeLabel(t, cls.type)}</span>
          <span className="mc-panel-name">{classDisplayName(cls, t)}</span>
        </div>
        <div className="mc-panel-meta">
          <PeopleCount n={cls.students.length} />
        </div>
      </header>
      <ClassDetails cls={cls} inline />
      <RosterTable students={cls.students} />
    </section>
  )
}

ClassPanel.propTypes = { cls: PropTypes.object.isRequired }

/* Admin class detail on mobile: summary card + roster list */
const ClassMobileDetail = ({ cls }) => {
  const { t } = useTranslation()
  return (
    <section className="mc-class-card">
      <div className="mc-class-summary">
        <TypeIcon type={cls.type} />
        <span className="mc-class-id">
          <span className="mc-class-type">{typeLabel(t, cls.type)}</span>
          <span className="mc-class-name">{classDisplayName(cls, t)}</span>
        </span>
        <PeopleCount n={cls.students.length} />
      </div>
      <div className="mc-class-body">
        <ClassDetails cls={cls} inline />
        <ul className="mc-roster">
          {cls.students.map((s) => (
            <StudentRow key={s.childId} student={s} />
          ))}
        </ul>
      </div>
    </section>
  )
}

ClassMobileDetail.propTypes = { cls: PropTypes.object.isRequired }

/* Student detail — mirrors what the family page shows per child:
   class, room, day, teachers per program, plus parents and location. */
const StudentDetail = ({ student }) => {
  const { t } = useTranslation()
  const { language } = useLanguage()
  return (
    <section className="mc-panel">
      <header className="mc-panel-head">
        <Avatar name={student.studentName} gender={student.gender} large />
        <span className="mc-panel-name">{student.studentName}</span>
        <div className="mc-panel-meta">
          <span>{formatBirthdate(student.birthdate, language)}</span>
          <span>{t('modules.age')} {ageOf(student.birthdate)}</span>
          <LocationBadge location={student.location} />
        </div>
      </header>
      <div className="mc-detail-body">
        <h3 className="mc-detail-label">{t('modules.parents')}</h3>
        <div className="mc-detail-parents">
          {parentPairs(student).map((p) => (
            <span key={`${p.name}:${p.phone}`} className="mc-parent-pair">
              {p.name} {p.phone && <a href={`tel:${p.phone}`}>{p.phone}</a>}
            </span>
          ))}
        </div>
        <h3 className="mc-detail-label">{t('modules.classes')}</h3>
        <div className="mc-card-grid">
          {student.classes.map((c) => (
            <div key={c.classId} className="mc-detail-class">
              <div className="mc-detail-class-head">
                <TypeIcon type={c.type} small />
                <span className="mc-class-id">
                  <span className="mc-class-type">{typeLabel(t, c.type)}</span>
                  <span className="mc-class-list-name">{classDisplayName(c, t)}</span>
                </span>
                <LocationBadge location={c.location} />
              </div>
              <dl className="mc-kv">
                <dt>{t('modules.room')}</dt>
                <dd>{c.classroom}</dd>
                <dt>{t('modules.day')}</dt>
                <dd>
                  <DayName day={c.day} />
                </dd>
                <dt>{teachersLabel(t, c)}</dt>
                <dd>{teacherNames(c).join(', ')}</dd>
              </dl>
            </div>
          ))}
          {student.classes.length === 0 && <p className="mc-empty">{t('modules.notEnrolled')}</p>}
        </div>
      </div>
    </section>
  )
}

StudentDetail.propTypes = { student: PropTypes.object.isRequired }

/* The schema's "class" value already IS the short identifier ("21", "1В",
   choir number "4") — the tile shows it directly; the type lives in the
   section header and tile color. */
const ClassBadge = ({ cls }) => (
  <span className={`mc-type-icon mc-class-badge mc-type-${cls.type}`}>{cls.class}</span>
)

ClassBadge.propTypes = { cls: PropTypes.object.isRequired }

const teacherPreview = (teachers, max = 3) => {
  const shown = teachers.slice(0, max).join(' \u00b7 ')
  const extra = teachers.length - max
  return extra > 0 ? `${shown} +${extra}` : shown
}

const ClassRow = ({ cls, onOpen, showType = false }) => {
  const { t } = useTranslation()
  return (
    <button type="button" className="mc-list-row" onClick={() => onOpen(cls.classId)}>
      <ClassBadge cls={cls} />
      <span className="mc-class-id">
        {showType && <span className="mc-class-type">{typeLabel(t, cls.type)}</span>}
        <span className="mc-class-list-name">{classDisplayName(cls, t)}</span>
      </span>
      <span className="mc-row-teachers">{teacherPreview(teacherNames(cls))}</span>
      <span className="mc-row-meta">
        <DayName day={cls.day} /> · {t('modules.room')} {cls.classroom}
      </span>
      <LocationBadge location={cls.location} />
      <PeopleCount n={cls.students.length} />
      <FaChevronRight className="mc-chevron" />
    </button>
  )
}

ClassRow.propTypes = {
  cls: PropTypes.object.isRequired,
  onOpen: PropTypes.func.isRequired,
  showType: PropTypes.bool,
}

const ClassListSection = ({ classes, typeFilter, onTypeFilter, onOpen }) => {
  const { t } = useTranslation()
  const filtered = classes.filter((c) => typeFilter === 'all' || c.type === typeFilter)
  return (
    <>
      <div className="mc-filter-chips">
        {['all', ...MODULE_TYPE_ORDER].map((type) => (
          <button
            key={type}
            type="button"
            className={`mc-filter-chip ${typeFilter === type ? 'mc-filter-chip-active' : ''}`}
            onClick={() => onTypeFilter(type)}
          >
            {type === 'all' ? t('modules.all') : typeLabel(t, type)}
          </button>
        ))}
      </div>
      {MODULE_TYPE_ORDER.map((type) => {
        const group = filtered.filter((c) => c.type === type)
        if (group.length === 0) return null
        return (
          <section key={type} className="mc-group">
            <h2 className="mc-group-head">
              <TypeIcon type={type} small />
              {typeLabel(t, type)}
              <span className="mc-group-count">{group.length}</span>
            </h2>
            <div className="mc-list">
              {group.map((c) => (
                <ClassRow key={c.classId} cls={c} onOpen={onOpen} />
              ))}
            </div>
          </section>
        )
      })}
    </>
  )
}

ClassListSection.propTypes = {
  classes: PropTypes.array.isRequired,
  typeFilter: PropTypes.string.isRequired,
  onTypeFilter: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
}

const FamiliesTable = ({ families, onOpen }) => {
  const { t } = useTranslation()
  return (
    <section className="mc-panel">
      <div className="mc-table-wrap">
        <table className="mc-table">
          <thead>
            <tr>
              <th>{t('common.family')}</th>
              <th>{t('modules.parents')}</th>
              <th>{t('modules.children')}</th>
              <th>{t('modules.location')}</th>
            </tr>
          </thead>
          <tbody>
            {families.map((f) => (
              <tr key={f.familyId} className="mc-row-click" onClick={() => onOpen(f.familyId)}>
                <td className="mc-td-name">{f.familyName}</td>
                <td className="mc-td-parents">
                  {parentPairs(f).map((p) => (
                    <span key={`${p.name}:${p.phone}`} className="mc-parent-pair">
                      {p.name}{' '}
                      {p.phone && (
                        <a href={`tel:${p.phone}`} onClick={(e) => e.stopPropagation()}>
                          {p.phone}
                        </a>
                      )}
                    </span>
                  ))}
                </td>
                <td>
                  <div className="mc-td-children">
                    {f.children.map((ch) => (
                      <span key={ch.childId} className="mc-child-name">
                        {givenName(ch.studentName)}
                        <GenderSymbol gender={ch.gender} />
                      </span>
                    ))}
                  </div>
                </td>
                <td>{f.location ? <LocationBadge location={f.location} /> : <span className="mc-muted">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

FamiliesTable.propTypes = { families: PropTypes.array.isRequired, onOpen: PropTypes.func.isRequired }

const FamilyRow = ({ family, onOpen }) => (
  <button type="button" className="mc-list-row" onClick={() => onOpen(family.familyId)}>
    <span className="mc-class-id">
      <span className="mc-class-list-name">{family.familyName}</span>
      <span className="mc-row-sub">{parentPairs(family).map((p) => p.name).join(' & ')}</span>
      <span className="mc-row-sub">
        {family.children.map((ch) => givenName(ch.studentName)).join(', ')}
      </span>
    </span>
    <LocationBadge location={family.location} />
    <PeopleCount n={family.children.length} />
    <FaChevronRight className="mc-chevron" />
  </button>
)

FamilyRow.propTypes = { family: PropTypes.object.isRequired, onOpen: PropTypes.func.isRequired }

/* Family detail — the children render through the family page's own
   StudentGroupedView (same cards parents see), fed by the admin data. */
const toActivityDetails = (c) =>
  c.type === 'childrensChoir'
    ? {
        choirNumber: c.class,
        choirRoom: c.classroom,
        day: c.day,
        choirLeaders: teacherNames(c),
      }
    : { class: c.class, classroom: c.classroom, teachers: teacherNames(c), day: c.day }

const FAMILY_VIEW_ACTIVITIES = [
  { id: 'sundaySchool', name: 'Sunday School', icon: 'FaBook' },
  { id: 'russianSchool', name: 'Russian School', icon: 'FaGlobeEurope' },
  { id: 'childrensChoir', name: "Children's Choir", icon: 'FaMusic' },
]

// Class document types map 1:1 onto the family view's activity ids.
const TYPE_TO_ACTIVITY = { sundaySchool: 'sundaySchool', russianSchool: 'russianSchool', childrensChoir: 'childrensChoir' }

const FamilyDetail = ({ family }) => {
  const { t } = useTranslation()
  const students = family.children.map((ch) => ({
    id: ch.childId,
    name: ch.studentName,
    birthdate: ch.birthdate,
    gender: ch.gender,
    activities: Object.entries(TYPE_TO_ACTIVITY).map(([type, activityId]) => {
      const cls = ch.classes.find((c) => c.type === type)
      return {
        id: activityId,
        status: cls ? 'completed' : 'pending',
        details: cls ? toActivityDetails(cls) : undefined,
      }
    }),
  }))

  return (
    <>
      <section className="mc-panel mc-family-head">
        <header className="mc-panel-head">
          <span className="mc-panel-name">{family.familyName}</span>
          <div className="mc-panel-meta">
            <LocationBadge location={family.location} />
            <PeopleCount n={family.children.length} />
          </div>
        </header>
        <div className="mc-detail-body">
          <h3 className="mc-detail-label">{t('modules.parents')}</h3>
          <div className="mc-detail-parents">
            {parentPairs(family).map((p) => (
              <span key={`${p.name}:${p.phone}`} className="mc-parent-pair">
                {p.name} {p.phone && <a href={`tel:${p.phone}`}>{p.phone}</a>}
              </span>
            ))}
          </div>
        </div>
      </section>
      <StudentGroupedView students={students} activities={FAMILY_VIEW_ACTIVITIES} />
    </>
  )
}

FamilyDetail.propTypes = { family: PropTypes.object.isRequired }

/* Smart search: one query, grouped hits across classes, families, students.
   Purely client-side over the admin payload — no extra backend. */
const RESULT_CAP = 8

const searchAdmin = (admin, query, words) => {
  const q = query.trim().toLowerCase()
  if (!q) return null
  const has = (str) => String(str || '').toLowerCase().includes(q)
  return {
    // class identity is the "class" value; match it bare ("21") and as the
    // display name in the active language ("Class 21" / "Класс 21"), plus
    // legacy teacher names and teacherV2.
    classes: admin.classes.filter(
      (c) =>
        has(c.class) ||
        has(classNameWith(c, words)) ||
        teacherNames(c).some(has) ||
        (c.teacherV2 || []).some((t) => has(t.teacherName))
    ),
    families: admin.families.filter(
      (f) => has(f.familyName) || parentPairs(f).some((p) => has(p.name))
    ),
    students: admin.students.filter((st) => has(st.studentName)),
  }
}

const SearchResults = ({ results, onOpenClass, onOpenFamily, onOpenStudent }) => {
  const { t } = useTranslation()
  const { language } = useLanguage()
  const groups = [
    { key: 'classes', label: t('modules.classes'), items: results.classes },
    { key: 'families', label: t('modules.families'), items: results.families },
    { key: 'students', label: t('modules.students'), items: results.students },
  ].filter((g) => g.items.length > 0)

  if (groups.length === 0) return <p className="mc-empty">{t('modules.noMatches')}</p>

  return groups.map((g) => (
    <section key={g.key} className="mc-group">
      <h2 className="mc-group-head">
        {g.label}
        <span className="mc-group-count">{g.items.length}</span>
      </h2>
      <div className="mc-list">
        {g.key === 'classes' &&
          g.items.slice(0, RESULT_CAP).map((c) => <ClassRow key={c.classId} cls={c} onOpen={onOpenClass} showType />)}
        {g.key === 'families' &&
          g.items.slice(0, RESULT_CAP).map((f) => <FamilyRow key={f.familyId} family={f} onOpen={onOpenFamily} />)}
        {g.key === 'students' &&
          g.items.slice(0, RESULT_CAP).map((st) => (
            <button
              type="button"
              key={st.childId}
              className="mc-list-row"
              onClick={() => onOpenStudent(st.childId)}
            >
              <Avatar name={st.studentName} gender={st.gender} />
              <span className="mc-class-id">
                <span className="mc-class-list-name">{st.studentName}</span>
                <span className="mc-row-sub">{formatBirthdate(st.birthdate, language)}</span>
              </span>
              <span className="mc-chips">
                {st.classes.map((c) => (
                  <span key={c.classId} className={`mc-chip mc-type-${c.type}`}>
                    {classDisplayName(c, t)}
                  </span>
                ))}
              </span>
              <LocationBadge location={st.location} />
              <FaChevronRight className="mc-chevron" />
            </button>
          ))}
      </div>
      {g.items.length > RESULT_CAP && (
        <p className="mc-more">{t('modules.moreResults', { n: g.items.length - RESULT_CAP })}</p>
      )}
    </section>
  ))
}

const AdminModule = ({ admin }) => {
  const { t } = useTranslation()
  const isDesktop = useIsDesktop()
  const [tab, setTab] = useState('classes')
  const [query, setQuery] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const [openClassId, setOpenClassId] = useState(null)
  const [openStudentId, setOpenStudentId] = useState(null)
  const [openFamilyId, setOpenFamilyId] = useState(null)

  const openClass = admin.classes.find((c) => c.classId === openClassId)
  const openStudent = admin.students.find((st) => st.childId === openStudentId)
  const openFamily = admin.families.find((f) => f.familyId === openFamilyId)
  const openDetail = openClass || openStudent || openFamily
  // The class words are plain strings, so they are stable memo deps that
  // change exactly when the language does.
  const { classWord, choirWord } = classWords(t)
  const results = useMemo(
    () => searchAdmin(admin, query, { classWord, choirWord }),
    [admin, query, classWord, choirWord]
  )
  const sortedFamilies = useMemo(
    () => [...admin.families].sort((a, b) => a.familyName.localeCompare(b.familyName)),
    [admin.families]
  )

  const closeDetails = () => {
    setOpenClassId(null)
    setOpenStudentId(null)
    setOpenFamilyId(null)
  }

  const openOnly = (setter) => (id) => {
    closeDetails()
    setter(id)
  }

  return (
    <div className="mc-page">
      <div className="mc-search-wrap">
        <FaSearch aria-hidden="true" />
        <input
          type="search"
          className="mc-search"
          placeholder={t('modules.searchAdmin')}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            closeDetails()
          }}
        />
      </div>

      {openDetail ? (
        <>
          <button type="button" className="mc-back" onClick={closeDetails}>
            <FaArrowLeft /> {t('modules.back')}
          </button>
          {openClass && (isDesktop ? <ClassPanel cls={openClass} /> : <ClassMobileDetail cls={openClass} />)}
          {openStudent && <StudentDetail student={openStudent} />}
          {openFamily && <FamilyDetail family={openFamily} />}
        </>
      ) : results ? (
        <SearchResults
          results={results}
          onOpenClass={openOnly(setOpenClassId)}
          onOpenFamily={openOnly(setOpenFamilyId)}
          onOpenStudent={openOnly(setOpenStudentId)}
        />
      ) : (
        <>
          <div className="mc-subnav" role="tablist">
            {[
              ['classes', `${t('modules.classes')} (${admin.classes.length})`],
              ['students', `${t('modules.students')} (${admin.students.length})`],
              ['families', `${t('modules.families')} (${admin.families.length})`],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                className={`mc-subtab ${tab === key ? 'mc-subtab-active' : ''}`}
                onClick={() => setTab(key)}
              >
                {label}
              </button>
            ))}
          </div>

          {tab === 'classes' && (
            <ClassListSection
              classes={admin.classes}
              typeFilter={typeFilter}
              onTypeFilter={setTypeFilter}
              onOpen={openOnly(setOpenClassId)}
            />
          )}

          {tab === 'students' &&
            (isDesktop ? (
              <section className="mc-panel">
                <RosterTable
                  students={admin.students}
                  showClasses
                  showLocation
                  onOpenStudent={openOnly(setOpenStudentId)}
                />
              </section>
            ) : (
              <ul className="mc-roster mc-roster-card">
                {admin.students.map((st) => (
                  <StudentRow
                    key={st.childId}
                    student={st}
                    classChips={st.classes}
                    showLocation
                    onOpen={openOnly(setOpenStudentId)}
                  />
                ))}
              </ul>
            ))}

          {tab === 'families' &&
            (isDesktop ? (
              <FamiliesTable families={sortedFamilies} onOpen={openOnly(setOpenFamilyId)} />
            ) : (
              <div className="mc-list">
                {sortedFamilies.map((f) => (
                  <FamilyRow key={f.familyId} family={f} onOpen={openOnly(setOpenFamilyId)} />
                ))}
              </div>
            ))}
        </>
      )}
    </div>
  )
}

AdminModule.propTypes = { admin: PropTypes.object.isRequired }

/* ---------------------------------------------------------------- */
/* Shell                                                            */
/* ---------------------------------------------------------------- */

const ModulesConcept = ({
  phoneNumber,
  familyView,
  hasFamily = false,
  teacherClasses = [],
  onSignOut,
}) => {
  const { t } = useTranslation()
  const payload = useMemo(() => {
    if (isUsingMockData()) {
      return resolveModules(phoneNumber) || resolveModules(DEMO_ALL_MODULES_PHONE)
    }
    const modules = {}
    if (hasFamily) modules.family = true
    if (teacherClasses.length > 0) modules.teacher = { classes: teacherClasses }
    const defaultModule = hasFamily ? 'family' : teacherClasses.length > 0 ? 'teacher' : null
    return defaultModule ? { defaultModule, modules } : { defaultModule: null, modules: {} }
  }, [phoneNumber, hasFamily, teacherClasses])

  // Nav destinations: Family, one item per taught class (no "Teacher"
  // button), Admin. Each carries the URL path it lives at. Labels are
  // resolved at render (navLabel/navSub) so they follow the language toggle
  // without invalidating this memo.
  const navItems = useMemo(() => {
    const items = []
    if (payload.modules.family) {
      items.push({ key: 'family', path: '/family', labelKey: 'common.family', icon: FaUserFriends })
    }
    if (payload.modules.teacher) {
      const usedSlugs = new Set()
      for (const cls of payload.modules.teacher.classes) {
        let slug = slugify(cls.classId) || 'class'
        while (usedSlugs.has(slug)) slug = `${slug}-2`
        usedSlugs.add(slug)
        items.push({
          key: `class:${cls.classId}`,
          path: `/class/${slug}`,
          icon: TYPE_ICONS[cls.type],
          cls,
        })
      }
    }
    if (payload.modules.admin) {
      items.push({ key: 'admin', path: '/admin', labelKey: 'modules.admin', icon: FaClipboardList })
    }
    return items
  }, [payload])

  const navLabel = (item) => (item.cls ? classDisplayName(item.cls, t) : t(item.labelKey))
  const navSub = (item) => (item.cls ? typeLabel(t, item.cls.type) : null)

  const defaultKey =
    payload.defaultModule === 'teacher'
      ? navItems.find((i) => i.cls)?.key
      : payload.defaultModule
  const defaultItem = navItems.find((i) => i.key === defaultKey) || navItems[0]

  // The URL is the source of truth for which page is open, so a refresh,
  // back/forward, or a shared link lands on the same page.
  const pathname = usePathname()
  const activeItem = navItems.find((i) => i.path === pathname) || defaultItem
  const active = activeItem.key
  const setActive = (key) => {
    const target = navItems.find((i) => i.key === key)
    if (target) navigate(target.path)
  }

  // Unknown path (e.g. "/" right after login): rewrite it to the page shown.
  useEffect(() => {
    if (pathname !== activeItem.path) navigate(activeItem.path, { replace: true })
  }, [pathname, activeItem.path])

  const [collapsed, setCollapsed] = useState(false)
  const hasNav = navItems.length > 1

  useEffect(() => {
    if (!navItems.some((i) => i.key === active) && navItems[0]) {
      setActive(navItems[0].key)
    }
  }, [navItems, active])

  // Unpin html/body from viewport height while the shell is mounted: the
  // global stylesheet fixes them to 100%, which makes the BODY the scroller
  // — and iOS Safari only collapses its toolbars when the DOCUMENT scrolls.
  useEffect(() => {
    document.documentElement.classList.add('mc-unlock')
    document.body.classList.add('mc-unlock')
    return () => {
      document.documentElement.classList.remove('mc-unlock')
      document.body.classList.remove('mc-unlock')
    }
  }, [])

  if (!activeItem) {
    return null
  }

  return (
    <div className={`mc-shell ${hasNav ? 'mc-has-nav' : ''} ${collapsed ? 'mc-collapsed' : ''}`}>
      {hasNav && (
        <aside className="mc-sidebar">
          <div className="mc-sidebar-brand">
            <img src="/logo.svg" alt={t('common.logoAlt')} />
            <span className="mc-nav-label">Ark of Salvation</span>
          </div>
          <nav className="mc-sidebar-nav" aria-label={t('modules.navigation')}>
            {navItems.map((item) => {
              const Icon = item.icon
              return (
                <button
                  key={item.key}
                  type="button"
                  className={`mc-side-item ${active === item.key ? 'mc-side-item-active' : ''}`}
                  onClick={() => setActive(item.key)}
                >
                  <Icon className="mc-side-icon" />
                  <span className="mc-nav-label mc-side-text">
                    {item.cls && <span className="mc-side-sub">{navSub(item)}</span>}
                    <span>{navLabel(item)}</span>
                  </span>
                </button>
              )
            })}
          </nav>
          <button
            type="button"
            className="mc-side-item mc-side-collapse"
            aria-label={collapsed ? t('modules.expandNav') : t('modules.collapseNav')}
            onClick={() => setCollapsed(!collapsed)}
          >
            {collapsed ? (
              <FaAngleDoubleRight className="mc-side-icon" />
            ) : (
              <FaAngleDoubleLeft className="mc-side-icon" />
            )}
            <span className="mc-nav-label">{t('modules.collapse')}</span>
          </button>
        </aside>
      )}

      <div className="mc-main">
        {activeItem.key === 'family' && familyView}
        {activeItem.cls && (
          <div className="mc-screen">
            <div className="mc-page mc-page-header">
              <HeaderCard title={classDisplayName(activeItem.cls, t)} subtitle={typeLabel(t, activeItem.cls.type)} onSignOut={onSignOut} />
            </div>
            <ClassPage cls={activeItem.cls} />
            <PageFoot onSignOut={onSignOut} />
          </div>
        )}
        {activeItem.key === 'admin' && payload.modules.admin && (
          <div className="mc-screen">
            <div className="mc-page mc-page-header">
              <HeaderCard title={t('modules.admin')} subtitle={t('modules.adminSubtitle')} onSignOut={onSignOut} />
            </div>
            <AdminModule admin={payload.modules.admin} />
            <PageFoot onSignOut={onSignOut} />
          </div>
        )}
      </div>

      {hasNav && (
        <nav className="mc-nav" aria-label={t('modules.navigation')}>
          {navItems.map((item) => {
            const Icon = item.icon
            return (
              <button
                key={item.key}
                type="button"
                className={`mc-nav-item ${active === item.key ? 'mc-nav-item-active' : ''}`}
                onClick={() => setActive(item.key)}
              >
                <Icon className="mc-nav-icon" />
                <span className="mc-nav-label">{navLabel(item)}</span>
              </button>
            )
          })}
        </nav>
      )}
    </div>
  )
}

ModulesConcept.propTypes = {
  phoneNumber: PropTypes.string,
  familyView: PropTypes.node,
  hasFamily: PropTypes.bool,
  teacherClasses: PropTypes.arrayOf(PropTypes.object),
  onSignOut: PropTypes.func,
}

export default ModulesConcept
